import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Dialog,
  DialogContent,
  Typography,
  Tooltip
} from '@mui/material';
import { 
  FiX, 
  FiRefreshCw, 
  FiFileText, 
  FiGrid, 
  FiFile, 
  FiCode, 
  FiImage
} from 'react-icons/fi';
import { formatFileSize, getFileIcon } from '../../libs/fileAttachmentUtils';

const iconMap = {
  FiFileText,
  FiGrid,
  FiFile,
  FiCode,
  FiImage
};

export const SortableAttachment = ({ 
  file, 
  onRemove, 
  onRetry,
  onPreview 
}) => {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: file.id,
    disabled: file.status === 'uploading'
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const IconComponent = iconMap[getFileIcon(file.name)] || FiFile;

  const handleClick = () => {
    if (file.isImage && file.status === 'done') {
      if (file.previewUrl) {
        setDialogOpen(true);
      } else if (file.uploadedUrl) {
        window.open(file.uploadedUrl, '_blank');
      }
    } else if (file.status === 'done' && file.uploadedUrl) {
      // Download non-image files
      const link = document.createElement('a');
      link.href = file.uploadedUrl;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const getStatusColor = () => {
    switch (file.status) {
      case 'done':
        return 'success';
      case 'error':
        return 'error';
      case 'uploading':
        return 'primary';
      default:
        return 'default';
    }
  };

  const getTooltipText = () => {
    switch (file.status) {
      case 'done':
        return file.isImage ? 'Click to preview' : 'Click to download';
      case 'error':
        return 'Upload failed - click retry button';
      case 'uploading':
        return `Uploading... ${file.progress}%`;
      default:
        return 'Pending upload';
    }
  };

  if (file.isImage && file.status !== 'error') {
    // Image thumbnail display
    return (
      <>
        <Box
          ref={setNodeRef}
          style={style}
          sx={{
            position: 'relative',
            width: 80,
            height: 80,
            borderRadius: 1,
            overflow: 'hidden',
            cursor: file.status === 'done' ? 'pointer' : 'default',
            border: theme => `2px solid ${theme.palette.grey[300]}`,
            '&:hover': {
              borderColor: 'primary.main'
            }
          }}
          {...attributes}
          {...listeners}
          onClick={handleClick}
        >
          <Tooltip title={getTooltipText()}>
            <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
              {file.previewUrl && (
                <img
                  src={file.previewUrl}
                  alt={file.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              )}
              
              {/* Progress overlay */}
              {file.status === 'uploading' && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column'
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'white', mb: 1 }}>
                    {file.progress}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={file.progress}
                    sx={{ width: '80%' }}
                  />
                </Box>
              )}
              
              {/* Remove button */}
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(file.id);
                }}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  backgroundColor: 'error.main',
                  color: 'white',
                  width: 20,
                  height: 20,
                  '&:hover': {
                    backgroundColor: 'error.dark'
                  }
                }}
              >
                <FiX size={12} />
              </IconButton>
              
              {/* Retry button for errors */}
              {file.status === 'error' && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRetry(file.id);
                  }}
                  sx={{
                    position: 'absolute',
                    bottom: 4,
                    right: 4,
                    backgroundColor: 'warning.main',
                    color: 'white',
                    width: 24,
                    height: 24,
                    '&:hover': {
                      backgroundColor: 'warning.dark'
                    }
                  }}
                >
                  <FiRefreshCw size={12} />
                </IconButton>
              )}
            </Box>
          </Tooltip>
        </Box>

        {/* Image preview dialog */}
        <Dialog 
          open={dialogOpen} 
          onClose={() => setDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ position: 'relative' }}>
              <img
                src={file.previewUrl || file.uploadedUrl}
                alt={file.name}
                style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '80vh',
                  objectFit: 'contain'
                }}
              />
              <IconButton
                onClick={() => setDialogOpen(false)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.7)'
                  }
                }}
              >
                <FiX />
              </IconButton>
            </Box>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Non-image file display as chip
  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{ 
        position: 'relative',
        display: 'inline-block'
      }}
      {...attributes}
      {...listeners}
    >
      <Tooltip title={getTooltipText()}>
        <Chip
          icon={<IconComponent size={16} />}
          label={
            <Box>
              <Typography variant="caption" noWrap sx={{ maxWidth: 120 }}>
                {file.name}
              </Typography>
              <Typography variant="caption" display="block" sx={{ fontSize: '0.65rem', opacity: 0.7 }}>
                {formatFileSize(file.size)}
              </Typography>
            </Box>
          }
          color={getStatusColor()}
          variant={file.status === 'done' ? 'filled' : 'outlined'}
          onClick={handleClick}
          sx={{
            height: 'auto',
            py: 1,
            px: 1,
            cursor: file.status === 'done' ? 'pointer' : 'default',
            '& .MuiChip-label': {
              display: 'block',
              whiteSpace: 'normal'
            }
          }}
        />
      </Tooltip>
      
      {/* Progress bar for uploading files */}
      {file.status === 'uploading' && (
        <LinearProgress
          variant="determinate"
          value={file.progress}
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 2
          }}
        />
      )}
      
      {/* Remove button */}
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(file.id);
        }}
        sx={{
          position: 'absolute',
          top: -8,
          right: -8,
          backgroundColor: 'error.main',
          color: 'white',
          width: 20,
          height: 20,
          zIndex: 1,
          '&:hover': {
            backgroundColor: 'error.dark'
          }
        }}
      >
        <FiX size={12} />
      </IconButton>
      
      {/* Retry button for errors */}
      {file.status === 'error' && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onRetry(file.id);
          }}
          sx={{
            position: 'absolute',
            top: -8,
            left: -8,
            backgroundColor: 'warning.main',
            color: 'white',
            width: 20,
            height: 20,
            zIndex: 1,
            '&:hover': {
              backgroundColor: 'warning.dark'
            }
          }}
        >
          <FiRefreshCw size={12} />
        </IconButton>
      )}
    </Box>
  );
};